import React, { useEffect, useState } from "react";
import { Check, LogOut, Monitor, Shield, Smartphone, X } from "lucide-react";

interface LogoutModalProps {
    isOpen: boolean;
    onConfirm: (logoutAllDevices: boolean) => void;
    onCancel: () => void;
    anonymousBrowsing?: boolean;
}

export default function LogoutModal({ isOpen, onConfirm, onCancel, anonymousBrowsing = false }: LogoutModalProps) {
    const [selectedOption, setSelectedOption] = useState<"current" | "all">("current");
    const [isVisible, setIsVisible] = useState(false);

    const classes = (...xs: (string | false | null | undefined)[]) => xs.filter(Boolean).join(" ");

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onCancel();
        };

        if (isOpen) {
            document.addEventListener("keydown", handleKeyDown);
            setSelectedOption("current");
            setIsVisible(true);
        } else {
            const timer = window.setTimeout(() => setIsVisible(false), 220);
            return () => window.clearTimeout(timer);
        }

        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onCancel]);

    if (!isVisible && !isOpen) return null;

    const optionClass = (active: boolean) =>
        [
            "bs-session-logout-choice",
            active && "is-selected",
        ].filter(Boolean).join(" ");

    return (
        <div
            className={classes(
                "bs-session-logout-overlay",
                isOpen ? "is-open" : "is-closing"
            )}
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) onCancel();
            }}
        >
            <div
                className={classes(
                    "bs-session-logout-panel",
                    isOpen ? "is-open" : "is-closing"
                )}
                role="dialog"
                aria-modal="true"
                aria-labelledby="logout-title"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className="bs-session-logout-halo" />
                <div className="bs-session-logout-card">
                    <header className="bs-session-logout-header">
                        <div>
                            <h2 id="logout-title">{anonymousBrowsing ? "Browse anonymously" : "Logout"}</h2>
                            <p>
                                {anonymousBrowsing ? "Sign out on this browser and continue as a guest." : "Choose where this session ends."}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={onCancel}
                            className="bs-session-logout-close"
                            aria-label="Close logout dialog"
                        >
                            <X size={18} />
                        </button>
                    </header>

                    <div className="bs-session-logout-choices">
                        <button type="button" onClick={() => setSelectedOption("current")} aria-pressed={selectedOption === "current"} className={`${optionClass(selectedOption === "current")} is-current`}>
                            <span className="bs-session-logout-choice-state" aria-hidden="true">
                                {selectedOption === "current" ? <Check size={13} strokeWidth={3} /> : null}
                            </span>
                            <span className="bs-session-logout-choice-icon">
                                <Monitor size={17} />
                            </span>
                            <span className="bs-session-logout-choice-copy">
                                <span className="bs-session-logout-choice-title">This device</span>
                                <span className="bs-session-logout-choice-accent" />
                                <span className="bs-session-logout-choice-text">Keep other devices signed in.</span>
                            </span>
                        </button>

                        {!anonymousBrowsing && <button type="button" onClick={() => setSelectedOption("all")} aria-pressed={selectedOption === "all"} className={`${optionClass(selectedOption === "all")} is-all-devices`}>
                            <span className="bs-session-logout-choice-state" aria-hidden="true">
                                {selectedOption === "all" ? <Check size={13} strokeWidth={3} /> : null}
                            </span>
                            <span className="bs-session-logout-choice-icon">
                                <Smartphone size={17} />
                            </span>
                            <span className="bs-session-logout-choice-copy">
                                <span className="bs-session-logout-choice-title">All devices</span>
                                <span className="bs-session-logout-choice-accent" />
                                <span className="bs-session-logout-choice-text">Revoke every active session.</span>
                            </span>
                        </button>}
                    </div>

                    <div className="bs-session-logout-note">
                        <Shield size={15} />
                        <span>{anonymousBrowsing ? "Your account and other sessions stay intact." : "You may need to sign in again on other devices."}</span>
                    </div>

                    <footer className="bs-session-logout-actions">
                        <button type="button" onClick={onCancel} className="bs-session-logout-cancel">
                            Cancel
                        </button>
                        <button type="button" onClick={() => onConfirm(!anonymousBrowsing && selectedOption === "all")} className="bs-session-logout-confirm">
                            <LogOut size={16} />
                            {anonymousBrowsing ? "Browse anonymously" : "Logout"}
                        </button>
                    </footer>
                </div>
            </div>
        </div>
    );
}
